import { Component, OnInit, OnDestroy } from '@angular/core';
import { interval, Subscription } from 'rxjs';

interface ProcessStep {
  id: number;
  icon: string;
  title: string;
  description: string;
  color: string;
  position: 'left' | 'right';
}

interface ShippingFeature {
  icon: string;
  title: string;
  description: string;
  colorClass: string;
}

@Component({
  selector: 'app-ship-a-box',
  templateUrl: './ship-a-box.component.html',
  styleUrls: ['./ship-a-box.component.scss'],
})
export class ShipABoxComponent implements OnInit, OnDestroy {
  activeStep = 0;
  isAnimating = false;
  autoAdvanceSub!: Subscription;
  hoveredFeature: number | null = null;

  processSteps: ProcessStep[] = [
    {
      id: 1,
      icon: '/assets/img/box-journey/booking.png',
      title: 'BOOKING',
      description:
        'Your balikbayan box journey begins when you schedule a pick-up via the LOGO Mobile App, call our customer service, or drop it off at any of our LOGO Stores, with each box tagged using a QR code',
      color: 'green',
      position: 'left',
    },
    {
      id: 2,
      icon: '/assets/img/box-journey/warehouse.png',
      title: 'WAREHOUSE',
      description:
        'All boxes collected from pick-up and drop-off points are transported to out central warehouse, where they are scanned, documented, and prepared for loading.',
      color: 'blue',
      position: 'right',
    },
    {
      id: 3,
      icon: '/assets/img/box-journey/container-loading.png',
      title: 'CONTAINER LOADING',
      description:
        'Once sorted, the boxes are loaded into shipping containers, securely arranged for transport to the port',
      color: 'green',
      position: 'left',
    },
    {
      id: 4,
      icon: '/assets/img/box-journey/seaport-transport.png',
      title: 'SEAPORT TRANSPORT',
      description:
        'At the port, containers are transferred from mother vessel to transhipment ports, then to feeder vessels enroute to the Philippines.',
      color: 'blue',
      position: 'right',
    },
    {
      id: 5,
      icon: '/assets/img/box-journey/boc.png',
      title: 'BOC (Bureau of Customs)',
      description:
        'At the port, containers are transferred from mother vessels to transhipment ports, then to feeder vessels enroute to the Philippines.',
      color: 'green',
      position: 'left',
    },
    {
      id: 6,
      icon: '/assets/img/box-journey/manila-warehouse.png',
      title: 'MANILA WAREHOUSE',
      description:
        'Cleared Shipment are received in our Manila warehouse, where each box is scanned again and sorted based on destination-either for Luzon delivery or transfer to our VISMIN hubs.',
      color: 'blue',
      position: 'right',
    },
    {
      id: 7,
      icon: '/assets/img/box-journey/dispatch-and-trucking.png',
      title: 'DISPATCH & TRUCKING',
      description:
        'After sorting, boxes are assigned to designated trucks and prepared for dispatch, read to reach every corner of the Philippines.',
      color: 'green',
      position: 'left',
    },
    {
      id: 8,
      icon: '/assets/img/box-journey/consignee.png',
      title: 'CONSIGNEE DELIVERY',
      description:
        'Finally, the consignee receivs the balikbayan box, bringing your love and care safely into their hands because every box tells a story, and we make sure it&apos; delivered',
      color: 'purple',
      position: 'right',
    },
  ];

  features: ShippingFeature[] = [
    {
      icon: 'bi-currency-dollar',
      title: 'Affordable Flat Rates',
      description: 'Transparent pricing with no hidden fees',
      colorClass: 'feature-green',
    },
    {
      icon: 'bi-shield-check',
      title: 'Professional Service',
      description: 'Expert handling with guaranteed reliability',
      colorClass: 'feature-blue',
    },
    {
      icon: 'bi-truck',
      title: 'Fast Ocean Transit',
      description: 'Optimized routes for quickest delivery',
      colorClass: 'feature-purple',
    },
    {
      icon: 'bi-phone',
      title: 'Box Tracking via Mobile App',
      description: 'Track your shipments anytime, anywhere',
      colorClass: 'feature-orange',
    },
    {
      icon: 'bi-person-check',
      title: 'No Third Party Service',
      description: 'Direct service with full accountability',
      colorClass: 'feature-teal',
    },
    {
      icon: 'bi-geo-alt',
      title: 'Real-time Tracking',
      description: 'Live updates on your shipment location',
      colorClass: 'feature-pink',
    },
  ];

  ngOnInit(): void {
    // Auto-advance every 4 seconds
    this.autoAdvanceSub = interval(4000).subscribe(() => {
      this.isAnimating = true;
      setTimeout(() => {
        this.activeStep = (this.activeStep + 1) % this.processSteps.length;
        this.isAnimating = false;
      }, 200);
    });
  }

  ngOnDestroy(): void {
    if (this.autoAdvanceSub) {
      this.autoAdvanceSub.unsubscribe();
    }
  }

  setActiveStep(index: number): void {
    this.isAnimating = true;
    setTimeout(() => {
      this.activeStep = index;
      this.isAnimating = false;
    }, 200);
  }

  isActive(index: number): boolean {
    return this.activeStep === index;
  }

  getProgressPercentage(): number {
    return Math.round(((this.activeStep + 1) / this.processSteps.length) * 100);
  }

  getStepCardClasses(index: number) {
    const step = this.processSteps[index];
    return {
      active: this.isActive(index),
      'animate-scale': this.isAnimating && this.isActive(index),
      [step.color]: this.isActive(index),
    };
  }

  getStepIconClasses(index: number) {
    const step = this.processSteps[index];
    return this.isActive(index) ? step.color : 'default';
  }

  getStepTextClasses(index: number) {
    const step = this.processSteps[index];
    return this.isActive(index) ? 'text-' + step.color : 'text-muted';
  }

  getTimelineDotClasses(index: number) {
    const step = this.processSteps[index];
    return {
      active: this.isActive(index),
      [step.color]: this.isActive(index),
    };
  }

  getNavButtonClasses(index: number) {
    const step = this.processSteps[index];
    return {
      active: this.isActive(index),
      [step.color + '-bg']: this.isActive(index),
    };
  }

  onFeatureHover(index: number): void {
    this.hoveredFeature = index;
  }

  onFeatureLeave(): void {
    this.hoveredFeature = null;
  }
}
